'use client';

import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft,
  Pencil,
  Link2,
  Check,
  Smartphone,
  Monitor,
  Tablet,
  ChevronRight,
  X,
  Camera,
  Loader2,
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface UserProfileScreenProps {
  onBack: () => void;
}

interface LinkedDevice {
  id: string;
  name: string;
  type: 'phone' | 'desktop' | 'tablet';
  lastActive: string;
  isCurrent?: boolean;
}

const LINKED_DEVICES: LinkedDevice[] = [
  { id: '1', name: 'iPhone 15 Pro', type: 'phone', lastActive: 'Active now', isCurrent: true },
  { id: '2', name: 'MacBook Pro', type: 'desktop', lastActive: '2 hours ago' },
  { id: '3', name: 'iPad Air', type: 'tablet', lastActive: 'Yesterday' },
];

const STATUS_OPTIONS = ['Online', 'Away', 'Busy', 'Invisible'];

const statusColor: Record<string, string> = {
  Online: '#4ADE80',
  Away: '#FBBF24',
  Busy: '#F87171',
  Invisible: '#6B7280',
};

export default function UserProfileScreen({ onBack }: UserProfileScreenProps) {
  const { user } = useAuth();
  const supabase = createClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [copied, setCopied] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Profile data
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [status, setStatus] = useState('Online');
  const [bio, setBio] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  // Edit state
  const [editName, setEditName] = useState('');
  const [editUsername, setEditUsername] = useState('');
  const [editStatus, setEditStatus] = useState('Online');
  const [editBio, setEditBio] = useState('');
  const [showStatusPicker, setShowStatusPicker] = useState(false);

  // Load profile from Supabase
  useEffect(() => {
    if (!user) return;
    async function loadProfile() {
      setLoading(true);
      try {
        const { data, error: fetchError } = await supabase
          .from('user_profiles')
          .select('full_name, username, avatar_url, status_message, is_online')
          .eq('id', user.id)
          .single();

        if (fetchError) throw fetchError;

        if (data) {
          const name = data.full_name || '';
          const uname = data.username || '';
          const statusVal = data.is_online ? 'Online' : 'Away';
          const bioVal = data.status_message || '';
          const avatar = data.avatar_url || null;

          setDisplayName(name);
          setUsername(uname);
          setStatus(statusVal);
          setBio(bioVal);
          setAvatarUrl(avatar);

          setEditName(name);
          setEditUsername(uname);
          setEditStatus(statusVal);
          setEditBio(bioVal);
        }
      } catch (err: any) {
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    }
    loadProfile();
  }, [user]);

  const profileLink = `https://chatnova7985.builtwithrocket.new/u/${username || user?.id}`;

  function handleCopyLink() {
    navigator.clipboard.writeText(profileLink).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleSaveEdit() {
    if (!user) return;
    setSaving(true);
    setError(null);
    try {
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({
          full_name: editName.trim() || displayName,
          username: editUsername.trim() || username,
          is_online: editStatus === 'Online',
          status_message: editBio.trim(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setDisplayName(editName.trim() || displayName);
      setUsername(editUsername.trim() || username);
      setStatus(editStatus);
      setBio(editBio.trim());
      setEditMode(false);
      setShowStatusPicker(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  }

  function handleCancelEdit() {
    setEditName(displayName);
    setEditUsername(username);
    setEditStatus(status);
    setEditBio(bio);
    setEditMode(false);
    setShowStatusPicker(false);
    setError(null);
  }

  function handleAvatarClick() {
    if (editMode && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }

  async function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    setUploadingAvatar(true);
    setError(null);
    try {
      const ext = file.name.split('.').pop();
      const filePath = `${user.id}/avatar.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const publicUrl = urlData.publicUrl + `?t=${Date.now()}`;

      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ avatar_url: publicUrl, updated_at: new Date().toISOString() })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setAvatarUrl(publicUrl);
    } catch (err: any) {
      setError(err.message || 'Failed to upload photo');
    } finally {
      setUploadingAvatar(false);
      // Reset input so same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function DeviceIcon({ type }: { type: LinkedDevice['type'] }) {
    const style = { color: 'var(--primary)' };
    if (type === 'desktop') return <Monitor size={18} style={style} />;
    if (type === 'tablet') return <Tablet size={18} style={style} />;
    return <Smartphone size={18} style={style} />;
  }

  const initials = displayName
    ? displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : (user?.email?.[0] ?? '?').toUpperCase();

  return (
    <div
      className="flex flex-col h-screen w-full overflow-hidden"
      style={{ background: 'var(--background)' }}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        className="hidden"
        onChange={handleAvatarChange}
        aria-label="Upload profile photo"
      />

      {/* Header */}
      <header
        className="chat-header-surface flex items-center justify-between px-2 sm:px-3 flex-shrink-0 z-20 relative"
        style={{ height: '64px', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
      >
        <div className="flex items-center gap-1">
          <button
            onClick={onBack}
            className="flex items-center justify-center w-[44px] h-[44px] rounded-full hover:bg-white/8 transition-colors duration-150 active:scale-95 flex-shrink-0"
            aria-label="Go back"
          >
            <ArrowLeft size={22} className="text-white" />
          </button>
          <h1 className="text-[17px] font-semibold text-white tracking-[0.01em]">My Profile</h1>
        </div>
        <button
          onClick={editMode ? handleCancelEdit : () => setEditMode(true)}
          className="flex items-center gap-1.5 px-3 h-[36px] rounded-full transition-all duration-150 active:scale-95"
          style={{
            background: editMode ? 'rgba(239,68,68,0.15)' : 'rgba(40,127,216,0.18)',
            color: editMode ? '#F87171' : 'var(--primary)',
          }}
          aria-label={editMode ? 'Cancel editing' : 'Edit profile'}
        >
          {editMode ? <X size={16} /> : <Pencil size={15} />}
          <span className="text-[13px] font-semibold">{editMode ? 'Cancel' : 'Edit'}</span>
        </button>
      </header>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 size={28} className="animate-spin" style={{ color: 'var(--primary)' }} />
          </div>
        ) : (
          <>
            {/* Hero section */}
            <div
              className="flex flex-col items-center pt-8 pb-6 px-4"
              style={{ background: 'linear-gradient(180deg, rgba(40,127,216,0.14) 0%, transparent 100%)' }}
            >
              {/* Avatar */}
              <div className="relative mb-4">
                <div
                  className="w-[96px] h-[96px] rounded-full flex items-center justify-center text-white font-bold text-[32px] shadow-lg overflow-hidden"
                  style={{ background: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)' }}
                  aria-label="Your profile picture"
                >
                  {avatarUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={avatarUrl}
                      alt="Profile photo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    initials
                  )}
                </div>
                {/* Status dot */}
                <span
                  className="absolute bottom-[4px] right-[4px] w-[16px] h-[16px] rounded-full border-2"
                  style={{ background: statusColor[status] ?? '#4ADE80', borderColor: 'var(--background)' }}
                  aria-label={`Status: ${status}`}
                />
                {/* Camera overlay when editing */}
                {editMode && (
                  <button
                    onClick={handleAvatarClick}
                    disabled={uploadingAvatar}
                    className="absolute inset-0 rounded-full flex items-center justify-center transition-opacity duration-150"
                    style={{ background: 'rgba(0,0,0,0.52)' }}
                    aria-label="Change profile photo"
                  >
                    {uploadingAvatar ? (
                      <Loader2 size={24} className="text-white animate-spin" />
                    ) : (
                      <Camera size={24} className="text-white" />
                    )}
                  </button>
                )}
              </div>

              {/* Display name */}
              {editMode ? (
                <input
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  className="text-center text-[22px] font-bold text-white tracking-[0.01em] mb-1 bg-transparent border-b-2 outline-none w-full max-w-[240px]"
                  style={{ borderColor: 'var(--primary)' }}
                  placeholder="Display name"
                  aria-label="Edit display name"
                />
              ) : (
                <h2 className="text-[22px] font-bold text-white tracking-[0.01em] mb-1">
                  {displayName || user?.email?.split('@')[0] || 'User'}
                </h2>
              )}

              {/* Username */}
              {editMode ? (
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-[14px]" style={{ color: '#91A3B7' }}>@</span>
                  <input
                    value={editUsername}
                    onChange={e => setEditUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                    className="text-[14px] bg-transparent border-b outline-none"
                    style={{ color: '#91A3B7', borderColor: 'rgba(145,163,183,0.4)', minWidth: 0, width: `${Math.max(editUsername.length, 8)}ch` }}
                    placeholder="username"
                    aria-label="Edit username"
                  />
                </div>
              ) : (
                <p className="text-[14px]" style={{ color: '#91A3B7' }}>
                  {username ? `@${username}` : user?.email || ''}
                </p>
              )}

              {/* Status badge */}
              <button
                onClick={editMode ? () => setShowStatusPicker(v => !v) : undefined}
                className="flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full transition-all duration-150"
                style={{
                  background: 'rgba(255,255,255,0.07)',
                  cursor: editMode ? 'pointer' : 'default',
                  border: editMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid transparent',
                }}
                aria-label={editMode ? 'Change status' : `Status: ${status}`}
              >
                <span
                  className="w-[8px] h-[8px] rounded-full flex-shrink-0"
                  style={{ background: statusColor[editMode ? editStatus : status] ?? '#4ADE80' }}
                />
                <span className="text-[13px] font-medium" style={{ color: 'rgba(255,255,255,0.75)' }}>
                  {editMode ? editStatus : status}
                </span>
                {editMode && <ChevronRight size={13} style={{ color: '#91A3B7' }} />}
              </button>

              {/* Status picker dropdown */}
              {showStatusPicker && (
                <div
                  className="mt-2 rounded-2xl overflow-hidden shadow-xl"
                  style={{ background: 'var(--card)', border: '1px solid var(--border)', minWidth: '160px' }}
                >
                  {STATUS_OPTIONS.map(opt => (
                    <button
                      key={opt}
                      onClick={() => { setEditStatus(opt); setShowStatusPicker(false); }}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 hover:bg-white/8 transition-colors duration-100"
                    >
                      <span className="w-[8px] h-[8px] rounded-full flex-shrink-0" style={{ background: statusColor[opt] }} />
                      <span className="text-[14px] text-white">{opt}</span>
                      {editStatus === opt && <Check size={14} style={{ color: 'var(--primary)', marginLeft: 'auto' }} />}
                    </button>
                  ))}
                </div>
              )}

              {/* Error message */}
              {error && (
                <p className="mt-3 text-[13px] text-red-400 text-center px-4">{error}</p>
              )}
            </div>

            {/* Content cards */}
            <div className="px-4 space-y-3 pb-6">

              {/* Profile link */}
              <SectionLabel>Profile Link</SectionLabel>
              <InfoCard>
                <div className="flex items-center gap-3 px-4 py-3.5">
                  <span
                    className="w-[32px] h-[32px] rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(255,255,255,0.06)' }}
                  >
                    <Link2 size={16} style={{ color: 'var(--primary)' }} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-widest mb-0.5" style={{ color: '#91A3B7' }}>
                      Your link
                    </p>
                    <p className="text-[13px] truncate" style={{ color: 'rgba(255,255,255,0.7)' }}>
                      {profileLink}
                    </p>
                  </div>
                  <button
                    onClick={handleCopyLink}
                    className="flex items-center gap-1.5 px-3 h-[32px] rounded-full flex-shrink-0 transition-all duration-150 active:scale-95"
                    style={{
                      background: copied ? 'rgba(74,222,128,0.15)' : 'rgba(40,127,216,0.18)',
                      color: copied ? '#4ADE80' : 'var(--primary)',
                    }}
                    aria-label="Copy profile link"
                  >
                    {copied ? <Check size={14} /> : <Link2 size={14} />}
                    <span className="text-[12px] font-semibold">{copied ? 'Copied!' : 'Copy'}</span>
                  </button>
                </div>
              </InfoCard>

              {/* Bio */}
              <SectionLabel>About</SectionLabel>
              <InfoCard>
                <div className="px-4 py-3.5">
                  {editMode ? (
                    <textarea
                      value={editBio}
                      onChange={e => setEditBio(e.target.value)}
                      rows={3}
                      className="w-full bg-transparent text-[14px] leading-relaxed outline-none resize-none"
                      style={{ color: 'rgba(255,255,255,0.85)', caretColor: 'var(--primary)' }}
                      placeholder="Write something about yourself…"
                      aria-label="Edit bio"
                    />
                  ) : (
                    <p className="text-[14px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
                      {bio || <span style={{ color: '#91A3B7' }}>No bio yet.</span>}
                    </p>
                  )}
                </div>
              </InfoCard>

              {/* Linked devices */}
              <SectionLabel>Linked Devices</SectionLabel>
              <InfoCard>
                {LINKED_DEVICES.map((device, idx) => (
                  <React.Fragment key={device.id}>
                    {idx > 0 && <Divider />}
                    <div className="flex items-center gap-3 px-4 py-3.5">
                      <span
                        className="w-[32px] h-[32px] rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: 'rgba(255,255,255,0.06)' }}
                      >
                        <DeviceIcon type={device.type} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-[14px] font-medium text-white truncate">{device.name}</p>
                          {device.isCurrent && (
                            <span
                              className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0"
                              style={{ background: 'rgba(40,127,216,0.2)', color: 'var(--primary)' }}
                            >
                              This device
                            </span>
                          )}
                        </div>
                        <p className="text-[12px] mt-0.5" style={{ color: '#91A3B7' }}>{device.lastActive}</p>
                      </div>
                      {!device.isCurrent && (
                        <button
                          className="text-[12px] font-medium px-2.5 py-1 rounded-full transition-colors duration-150 hover:bg-red-500/15 flex-shrink-0"
                          style={{ color: '#F87171' }}
                          aria-label={`Remove ${device.name}`}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </React.Fragment>
                ))}
              </InfoCard>

              {/* Save button when editing */}
              {editMode && (
                <button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="w-full h-[50px] rounded-2xl font-semibold text-[15px] text-white transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2"
                  style={{ background: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)', opacity: saving ? 0.7 : 1 }}
                >
                  {saving ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      <span>Saving…</span>
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              )}

              <div className="h-4" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Sub-components ── */

function InfoCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
    >
      {children}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-widest px-1 pt-1" style={{ color: '#91A3B7' }}>
      {children}
    </p>
  );
}

function Divider() {
  return <div style={{ height: '1px', background: 'var(--border)', margin: '0 16px' }} />;
}
